/* ==========================================================================
   Lost My Buds — Premium Header behavior
   ==========================================================================
   Root cause of the reported issues (for your reference):

   1. "Predictive search / suggestions don't work" — there was no fetch call
      to Shopify's predictive search endpoint at all; the markup had a panel
      but nothing populated it. Implemented below against
      `routes.predictive_search_url` (passed in via data-predictive-search-url),
      which is Shopify's supported JSON endpoint — no theme-editor or
      Search & Discovery app changes required.

   2. "Search dropdown is broken" — panel visibility now has real open/close
      state (`.is-open` + `hidden` toggling) driven from one place, plus
      proper click-outside / Escape / blur handling instead of relying on
      :focus-within, which was fighting the click on a result link.

   3. Debounced input (180ms) + request-id guarding so a slow, older request
      can never overwrite a newer one's results (classic race condition with
      predictive search-as-you-type).

   4. Hardening pass: added console.error logging on fetch failure (was
      silently swallowed before — you'd only see a generic "something went
      wrong" message with no way to tell why), a guard around each header's
      init so one bad section can't block the others, and an early
      console.warn if the predictive-search URL is missing/blank so a
      liquid-side misconfiguration is obvious in devtools instead of just
      "nothing happens".
   ========================================================================== */

(function () {
  "use strict";

  var DEBOUNCE_MS = 180;
  var MIN_QUERY_LENGTH = 2;
  var RESULT_LIMIT = 4;

  document.querySelectorAll("[data-lmb-header]").forEach(function (header) {
    try {
      initHeader(header);
    } catch (err) {
      console.error("[lmb-header] init failed for this header:", err);
    }
  });

  function initHeader(header) {
    initSticky(header);
    initDrawer(header);
    header.querySelectorAll("[data-lmb-search]").forEach(initSearch);
    header.querySelectorAll("[data-lmb-search]").forEach(initPlaceholderCycle);
    header.querySelectorAll(".lmb-icon-btn").forEach(initRipple);
  }

  /* ---------------------------------------------------------------- */
  /* Sticky shrink-on-scroll                                           */
  /* ---------------------------------------------------------------- */

  function initSticky(header) {
    if (!header.hasAttribute("data-lmb-sticky")) return;

    var ticking = false;
    var threshold = 24;

    function update() {
      var scrolled = window.scrollY > threshold;
      header.classList.toggle("lmb-header--scrolled", scrolled);
      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    update();
  }

  /* ---------------------------------------------------------------- */
  /* Drawer (mobile menu) with focus trap                              */
  /* ---------------------------------------------------------------- */

  function initDrawer(header) {
    var openBtn = header.querySelector("[data-lmb-drawer-open]");
    var drawer = document.querySelector("[data-lmb-drawer]");
    var overlay = document.querySelector("[data-lmb-drawer-overlay]");
    var closeBtn = drawer ? drawer.querySelector("[data-lmb-drawer-close]") : null;
    if (!openBtn || !drawer || !overlay) return;

    var lastFocused = null;

    function focusableEls() {
      return Array.prototype.slice.call(
        drawer.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
      );
    }

    function trapFocus(e) {
      if (e.key !== "Tab") return;
      var els = focusableEls();
      if (!els.length) return;
      var first = els[0];
      var last = els[els.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    function onKeydown(e) {
      if (e.key === "Escape") {
        closeDrawer();
      } else {
        trapFocus(e);
      }
    }

    function openDrawer() {
      lastFocused = document.activeElement;
      drawer.hidden = false;
      overlay.hidden = false;
      requestAnimationFrame(function () {
        drawer.classList.add("is-open");
        overlay.classList.add("is-open");
      });
      openBtn.setAttribute("aria-expanded", "true");
      document.documentElement.style.overflow = "hidden";
      document.addEventListener("keydown", onKeydown);
      var els = focusableEls();
      if (els.length) els[0].focus();
    }

    function closeDrawer() {
      drawer.classList.remove("is-open");
      overlay.classList.remove("is-open");
      openBtn.setAttribute("aria-expanded", "false");
      document.documentElement.style.overflow = "";
      document.removeEventListener("keydown", onKeydown);

      var duration = 520;
      setTimeout(function () {
        drawer.hidden = true;
        overlay.hidden = true;
      }, duration);

      if (lastFocused && typeof lastFocused.focus === "function") {
        lastFocused.focus();
      }
    }

    openBtn.addEventListener("click", openDrawer);
    if (closeBtn) closeBtn.addEventListener("click", closeDrawer);
    overlay.addEventListener("click", closeDrawer);
  }

  /* ---------------------------------------------------------------- */
  /* Animated cycling placeholder ("Search OnePlus Buds" → "Search       */
  /* boAt Airdopes" → ...) shown only while the box is empty and blurred */
  /* ---------------------------------------------------------------- */

  var PLACEHOLDER_WORDS = [
    "Search OnePlus Buds",
    "Search boAt Airdopes",
    "Search Noise Earbuds",
    "Search JBL Tune",
    "Search Realme Buds",
    "Search Oppo Enco"
  ];
  var CYCLE_INTERVAL_MS = 2200;
  var CYCLE_FADE_MS = 260;

  function initPlaceholderCycle(root) {
    var form = root.querySelector("[data-lmb-search-form]");
    var input = root.querySelector("[data-lmb-search-input]");
    if (!form || !input || PLACEHOLDER_WORDS.length === 0) return;

    var span = document.createElement("span");
    span.className = "lmb-search__placeholder-cycle";
    span.setAttribute("aria-hidden", "true");
    span.textContent = PLACEHOLDER_WORDS[0];
    input.insertAdjacentElement("afterend", span);

    var idx = 0;

    function updateVisibility() {
      var shouldShow = document.activeElement !== input && input.value.trim().length === 0;
      span.classList.toggle("is-hidden", !shouldShow);
    }

    function cycle() {
      if (span.classList.contains("is-hidden")) return; // saves a pointless swap while hidden
      idx = (idx + 1) % PLACEHOLDER_WORDS.length;
      span.classList.add("is-fading");
      setTimeout(function () {
        span.textContent = PLACEHOLDER_WORDS[idx];
        span.classList.remove("is-fading");
      }, CYCLE_FADE_MS);
    }

    updateVisibility();
    setInterval(cycle, CYCLE_INTERVAL_MS);

    input.addEventListener("focus", updateVisibility);
    input.addEventListener("blur", updateVisibility);
    input.addEventListener("input", updateVisibility);
  }

  /* ---------------------------------------------------------------- */
  /* Predictive search                                                 */
  /* ---------------------------------------------------------------- */

  function initSearch(root) {
    var form = root.querySelector("[data-lmb-search-form]");
    var input = root.querySelector("[data-lmb-search-input]");
    var clearBtn = root.querySelector("[data-lmb-search-clear]");
    var panel = root.querySelector("[data-lmb-search-panel]");
    var resultsEl = root.querySelector("[data-lmb-search-results]");
    var predictiveUrl = root.getAttribute("data-predictive-search-url");
    var searchUrl = root.getAttribute("data-search-url");
    var headerEl = root.closest("[data-lmb-header]");

    if (!form || !input || !clearBtn || !panel || !resultsEl) {
      console.warn("[lmb-search] one or more required elements missing inside this search block — markup may not match header-premium.liquid.", root);
      return;
    }

    if (!predictiveUrl) {
      console.warn("[lmb-search] data-predictive-search-url is empty. This comes from {{ routes.predictive_search_url }} in the liquid section — check that attribute rendered correctly on the live page.", root);
      return;
    }

    var debounceTimer = null;
    var requestId = 0;
    var activeIndex = -1;

    function openPanel() {
      panel.hidden = false;
      requestAnimationFrame(function () {
        panel.classList.add("is-open");
      });
      input.setAttribute("aria-expanded", "true");
      if (headerEl) headerEl.classList.add("lmb-header--search-open");
    }

    function closePanel() {
      panel.classList.remove("is-open");
      input.setAttribute("aria-expanded", "false");
      activeIndex = -1;
      if (headerEl) headerEl.classList.remove("lmb-header--search-open");
      setTimeout(function () {
        if (!panel.classList.contains("is-open")) panel.hidden = true;
      }, 260);
    }

    function toggleClear() {
      clearBtn.hidden = input.value.trim().length === 0;
    }

    function escapeHtml(str) {
      var div = document.createElement("div");
      div.textContent = str;
      return div.innerHTML;
    }

    /* Relevance scoring so results aren't just "whatever order the API sent
       them in" — an exact title match beats a starts-with match, which
       beats a whole-word match, which beats a loose substring match, which
       beats a match that only hit via vendor/type/tag and not the title. */
    function scoreMatch(title, query) {
      var t = (title || "").toLowerCase();
      var q = (query || "").toLowerCase().trim();
      if (!q) return 5;
      if (t === q) return 0;
      if (t.indexOf(q) === 0) return 1;
      var escapedQuery = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      var wordBoundary = new RegExp("\\b" + escapedQuery + "\\b", "i");
      if (wordBoundary.test(t)) return 2;
      if (t.indexOf(q) !== -1) return 3;
      return 4;
    }

    function rankProducts(products, query) {
      return products
        .map(function (p, i) {
          return { p: p, i: i, score: scoreMatch(p.title, query) };
        })
        .sort(function (a, b) {
          if (a.score !== b.score) return a.score - b.score;
          var lenDiff = (a.p.title || "").length - (b.p.title || "").length;
          if (lenDiff !== 0) return lenDiff;
          return a.i - b.i; // stable fallback, keeps API order as last tie-break
        })
        .map(function (x) {
          return x.p;
        });
    }

    function highlight(text, query) {
      var safe = escapeHtml(text);
      var safeQuery = query.trim();
      if (!safeQuery) return safe;
      var escapedQuery = safeQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      var re = new RegExp("(" + escapedQuery + ")", "ig");
      return safe.replace(re, "<mark>$1</mark>");
    }

    function renderLoading() {
      var rows = "";
      for (var i = 0; i < 4; i++) {
        rows +=
          '<li class="lmb-search__skeleton-row">' +
          '<div class="lmb-search__skeleton-thumb"></div>' +
          '<div class="lmb-search__skeleton-lines">' +
          '<div class="lmb-search__skeleton-line lmb-search__skeleton-line--w60"></div>' +
          '<div class="lmb-search__skeleton-line lmb-search__skeleton-line--w35"></div>' +
          "</div></li>";
      }
      resultsEl.innerHTML = rows;
    }

    function renderEmptyPrompt() {
      resultsEl.innerHTML =
        '<li class="lmb-search__state">Keep typing to search products…</li>';
    }

    function renderNoResults(query) {
      resultsEl.innerHTML =
        '<li class="lmb-search__state">No results for “' +
        escapeHtml(query) +
        '”. Try a different term.</li>';
    }

    function renderError() {
      resultsEl.innerHTML =
        '<li class="lmb-search__state">Something went wrong. Press Enter to search anyway.</li>';
    }

    function renderResults(products, query) {
      var ranked = rankProducts(products, query);

      if (!ranked.length) {
        renderNoResults(query);
        return;
      }

      var html = ranked
        .slice(0, RESULT_LIMIT)
        .map(function (product, index) {
          var imageUrl = product.image || (product.featured_image ? product.featured_image.url : "");
          var priceHtml = product.price ? '<span class="lmb-search__result-price">' + escapeHtml(product.price) + "</span>" : "";
          var compareHtml =
            product.compare_at_price_max &&
            product.compare_at_price_max > product.price_max
              ? '<span class="lmb-search__result-compare">' + escapeHtml(product.compare_at_price) + "</span>"
              : "";
          var unavailableHtml = product.available === false
            ? '<span class="lmb-search__result-unavailable">Sold out</span>'
            : "";
          var vendorHtml = product.vendor
            ? '<span class="lmb-search__result-vendor">' + escapeHtml(product.vendor) + "</span>"
            : "";

          return (
            '<li class="lmb-search__result" style="--lmb-i:' + index + '" role="presentation">' +
            '<a class="lmb-search__result-link" href="' + product.url + '" role="option" data-lmb-result-index="' + index + '">' +
            '<span class="lmb-search__result-image">' +
            (imageUrl ? '<img src="' + imageUrl + '" alt="" loading="lazy" width="52" height="52">' : "") +
            "</span>" +
            '<span class="lmb-search__result-body">' +
            vendorHtml +
            '<span class="lmb-search__result-title">' + highlight(product.title, query) + "</span>" +
            '<span class="lmb-search__result-meta">' + priceHtml + compareHtml + unavailableHtml + "</span>" +
            "</span></a></li>"
          );
        })
        .join("");

      html +=
        '<li role="presentation"><a class="lmb-search__view-all" href="' +
        searchUrl + "?q=" + encodeURIComponent(query) + '&type=product">' +
        "View all results</a></li>";

      resultsEl.innerHTML = html;
      activeIndex = -1;
    }

    function fetchResults(query) {
      var thisRequest = ++requestId;
      renderLoading();
      openPanel();

      var url =
        predictiveUrl +
        "?q=" + encodeURIComponent(query) +
        "&resources[type]=product" +
        "&resources[limit]=" + RESULT_LIMIT +
        "&resources[options][fields]=title,product_type,variants.title,vendor" +
        "&resources[options][unavailable_products]=last";

      fetch(url, { headers: { Accept: "application/json" } })
        .then(function (response) {
          if (!response.ok) {
            throw new Error("Predictive search request failed with status " + response.status);
          }
          return response.json();
        })
        .then(function (data) {
          if (thisRequest !== requestId) return; // stale response, ignore
          var products =
            (data && data.resources && data.resources.results && data.resources.results.products) || [];
          renderResults(products, query);
        })
        .catch(function (err) {
          if (thisRequest !== requestId) return;
          console.error('[lmb-search] predictive search request failed for query "' + query + '":', err);
          renderError();
        });
    }

    input.addEventListener("input", function () {
      toggleClear();
      var query = input.value.trim();

      clearTimeout(debounceTimer);

      if (query.length === 0) {
        closePanel();
        return;
      }

      if (query.length < MIN_QUERY_LENGTH) {
        renderEmptyPrompt();
        openPanel();
        return;
      }

      debounceTimer = setTimeout(function () {
        fetchResults(query);
      }, DEBOUNCE_MS);
    });

    input.addEventListener("focus", function () {
      if (input.value.trim().length >= MIN_QUERY_LENGTH && resultsEl.children.length) {
        openPanel();
      }
    });

    input.addEventListener("keydown", function (e) {
      var items = resultsEl.querySelectorAll(".lmb-search__result-link");
      if (!items.length) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        activeIndex = Math.min(activeIndex + 1, items.length - 1);
        setActive(items);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        activeIndex = Math.max(activeIndex - 1, 0);
        setActive(items);
      } else if (e.key === "Enter") {
        if (activeIndex > -1 && items[activeIndex]) {
          e.preventDefault();
          window.location.href = items[activeIndex].href;
        }
      } else if (e.key === "Escape") {
        closePanel();
        input.blur();
      }
    });

    function setActive(items) {
      items.forEach(function (item, i) {
        item.classList.toggle("is-active", i === activeIndex);
      });
      if (items[activeIndex]) {
        items[activeIndex].scrollIntoView({ block: "nearest" });
      }
    }

    clearBtn.addEventListener("click", function () {
      input.value = "";
      toggleClear();
      closePanel();
      input.focus();
    });

    form.addEventListener("submit", function (e) {
      if (input.value.trim().length === 0) {
        e.preventDefault();
      }
    });

    document.addEventListener("click", function (e) {
      if (!root.contains(e.target)) {
        closePanel();
      }
    });

    toggleClear();
  }

  /* ---------------------------------------------------------------- */
  /* Icon button ripple                                                */
  /* ---------------------------------------------------------------- */

  function initRipple(btn) {
    btn.addEventListener("pointerdown", function (e) {
      var rect = btn.getBoundingClientRect();
      var x = e.clientX - rect.left;
      var y = e.clientY - rect.top;
      btn.style.setProperty("--lmb-ripple-x", x + "px");
      btn.style.setProperty("--lmb-ripple-y", y + "px");
      btn.classList.remove("is-rippling");
      // Force reflow so the animation can restart on rapid clicks
      void btn.offsetWidth;
      btn.classList.add("is-rippling");
    });

    btn.addEventListener("animationend", function () {
      btn.classList.remove("is-rippling");
    });
  }
})();
